/**
 * Amazon product helpers.
 *
 * Products imported from an Amazon receipt (see components/QuickAddModal.js)
 * carry the Amazon listing title as their name, the ASIN in `sku`, the product
 * link in `url`, and the Amazon product photo downloaded into the media table.
 * These helpers turn that into the fields a marketplace listing needs.
 */

import { media } from "./api";

/** eBay caps listing titles at 80 characters. */
export const EBAY_TITLE_MAX = 80;

/** Placeholder descriptions written at import time that carry no real detail. */
const PLACEHOLDER_DESCRIPTIONS = [/^added from\b/i, /^imported from\b/i];

/** True when the product originated from an Amazon receipt import. */
export function isAmazonProduct(product) {
  if (!product) return false;
  if (typeof product.source === "string" && /amazon/i.test(product.source)) {
    return true;
  }
  return /amazon\.[a-z.]+/i.test(product.url || "");
}

/** Pull the ASIN from the stored SKU or product URL. */
export function extractAsin(product) {
  if (!product) return null;

  if (product.sku && /^[A-Z0-9]{10}$/.test(product.sku)) {
    return product.sku;
  }

  const match = (product.url || "").match(
    /amazon\.[a-z.]+.*?\/(?:dp|gp\/product)\/([A-Z0-9]{10})/i,
  );
  return match ? match[1].toUpperCase() : null;
}

/** Canonical Amazon product URL, or null if we can't determine the ASIN. */
export function getAmazonUrl(product) {
  const asin = extractAsin(product);
  if (asin) return `https://www.amazon.com/dp/${asin}`;
  return product?.url || null;
}

/**
 * Rewrite an Amazon CDN image URL to a larger rendition.
 *
 * Amazon encodes the requested size in the filename (`._SL500_.jpg`). eBay
 * wants at least 500px and recommends 1600px, so upgrade what we ask for.
 * Non-Amazon URLs pass through untouched.
 */
export function upgradeAmazonImage(url, size = 1600) {
  if (typeof url !== "string") return url;
  if (!/(?:media|images)-amazon\.com\/images\/I\//.test(url)) return url;
  return url.replace(/\._[^./]+_\./, `._SL${size}_.`);
}

/**
 * Trim a long Amazon title to eBay's 80-character limit.
 *
 * Cuts at a word boundary rather than mid-word, and prefers to end at a
 * separator (comma/dash/pipe) so the result reads as a complete phrase.
 */
export function truncateTitle(title, maxLength = EBAY_TITLE_MAX) {
  const clean = (title || "").replace(/\s+/g, " ").trim();
  if (clean.length <= maxLength) return clean;

  const head = clean.slice(0, maxLength);

  // Prefer the last natural separator if it keeps a reasonable amount of title.
  const separator = Math.max(
    head.lastIndexOf(", "),
    head.lastIndexOf(" - "),
    head.lastIndexOf(" | "),
  );
  if (separator > maxLength * 0.5) return head.slice(0, separator).trim();

  const space = head.lastIndexOf(" ");
  return (space > 0 ? head.slice(0, space) : head).trim();
}

/**
 * Derive feature bullets from an Amazon title.
 *
 * Amazon titles are packed feature lists ("Brand Widget, 3-Pack, Blue, Fits
 * 12in"). Splitting on separators recovers usable bullets. Returns [] when the
 * title isn't structured that way.
 */
export function deriveBullets(title, limit = 6) {
  if (!title) return [];

  return (title || "")
    .split(/\s*[,|]\s*|\s+[-–—]\s+/)
    .map((part) => part.replace(/\s+/g, " ").trim())
    .filter((part) => part.length >= 4 && part.length <= 90)
    .slice(1) // The first segment is the product name itself, not a feature.
    .slice(0, limit);
}

/** Best-guess brand: the leading capitalised words of an Amazon title. */
export function deriveBrand(title) {
  const match = (title || "")
    .trim()
    .match(/^([A-Z][\w&'’.-]*(?:\s+[A-Z][\w&'’.-]*)?)/);
  return match ? match[1].trim() : "";
}

/** Human-readable label for an internal condition value. */
const CONDITION_LABELS = {
  new: "New",
  like_new: "Like New / Open Box",
  good: "Used — Good",
  fair: "Used — Acceptable",
  poor: "For Parts / Not Working",
};

/**
 * Build the rows for the description's spec table from what we actually store.
 *
 * Rows with no value are dropped by the renderer, so this can offer a field
 * optimistically.
 */
export function buildSpecs(product, { brand, sku, fromAmazon }) {
  return [
    { label: "Brand", value: brand },
    { label: fromAmazon ? "ASIN" : "SKU", value: sku },
    {
      label: "Condition",
      value: CONDITION_LABELS[product?.condition] || CONDITION_LABELS.new,
    },
    {
      label: "Quantity available",
      value: product?.quantity > 1 ? String(product.quantity) : "",
    },
  ].filter((row) => row.value);
}

function isPlaceholderDescription(description) {
  const trimmed = (description || "").trim();
  if (!trimmed) return true;
  return PLACEHOLDER_DESCRIPTIONS.some((pattern) => pattern.test(trimmed));
}

/**
 * Load every image for a product as an absolute URL, primary first.
 *
 * Falls back to the product's primary image when the media lookup fails or
 * returns nothing.
 */
export async function loadProductImages(product, fallbackImageUrl = null) {
  let urls = [];

  if (product?.id) {
    try {
      const response = await media.list(product.id);
      // Axios wraps the body, and the worker wraps the rows: { data: { data: [...] } }
      const payload = response?.data;
      const rows = Array.isArray(payload) ? payload : payload?.data || [];
      urls = rows
        .slice()
        .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
        .map((row) => row.file_path)
        .filter(Boolean)
        .map((path) => media.getImageUrl(path));
    } catch (err) {
      console.log(
        "Failed to load product media, falling back to primary image",
      );
    }
  }

  if (urls.length === 0 && product?.image_path) {
    urls = [media.getImageUrl(product.image_path)];
  }
  if (urls.length === 0 && fallbackImageUrl) {
    urls = [fallbackImageUrl];
  }

  return urls.map((url) => upgradeAmazonImage(url));
}

/**
 * Assemble the listing fields for a product, enriched from its Amazon origin.
 *
 * @param {Object} product - Product entity
 * @param {Object} options
 * @param {string[]} options.images - Pre-loaded image URLs (skips the media fetch)
 * @param {string} options.fallbackImageUrl - Image to use if the product has no media
 * @returns {Promise<Object>} { title, fullTitle, description, bullets, images, sku, brand, sourceUrl, condition, price, fromAmazon }
 */
export async function buildListingDetails(product, options = {}) {
  const { images: providedImages, fallbackImageUrl = null } = options;

  const images =
    providedImages || (await loadProductImages(product, fallbackImageUrl));

  const fullTitle = (product?.name || "").trim();
  const fromAmazon = isAmazonProduct(product);
  const asin = extractAsin(product);

  // Amazon imports store a placeholder description, so build one from the
  // listing title instead of shipping "Added from Amazon" to buyers.
  const description = isPlaceholderDescription(product?.description)
    ? fullTitle
    : product.description;

  const sku = asin || product?.sku || "";
  const brand = fromAmazon ? deriveBrand(fullTitle) : "";

  return {
    title: truncateTitle(fullTitle),
    fullTitle,
    // The trimmed-off tail of a long Amazon title still reads as a useful
    // secondary line under the headline.
    subtitle: brand,
    description,
    bullets: fromAmazon ? deriveBullets(fullTitle) : [],
    specs: buildSpecs(product, { brand, sku, fromAmazon }),
    images,
    sku,
    brand,
    sourceUrl: fromAmazon ? getAmazonUrl(product) : product?.url || "",
    condition: product?.condition || "new",
    price: product?.list_sale_price || product?.retail_price || "",
    fromAmazon,
  };
}

export default {
  EBAY_TITLE_MAX,
  isAmazonProduct,
  extractAsin,
  getAmazonUrl,
  upgradeAmazonImage,
  truncateTitle,
  deriveBullets,
  deriveBrand,
  loadProductImages,
  buildSpecs,
  buildListingDetails,
};
